import React, { useCallback, useMemo } from "react";
import { SearchOption, AgGridReact, AgGrid } from "@jsq/ds/core";
import { EntitySearch } from "#components/Search/EntitySearch";
import { useGetDataVaultEntitiesLazyQuery } from "#graphql/supergraphHooks";
import { clientContext } from "#utils/graphql_utils";
import { CustomFilter } from "../CustomFilter";
import { EntityFilterParams } from "./types";

/**
 * AgGrid Entity Filter Component
 *
 * A wrapper around EntitySearch that integrates with AgGrid's filtering system.
 * This component handles AgGrid-specific logic like filter callbacks, model changes,
 * and dependent filter coordination. Supports both single and multiselect modes.
 *
 * @param filterProps - EntityFilterProps (extends AgGrid CustomFilterProps)
 * @returns An entity filter component integrated with AgGrid
 *
 * @example
 * // Single select mode (default)
 * {
 *   field: 'entityName',
 *   filter: EntityFilter,
 *   filterParams: {
 *     dependentFilter: 'clientName'
 *   }
 * }
 *
 * @example
 * // Multiselect mode
 * {
 *   field: 'entityName',
 *   filter: EntityFilter,
 *   filterParams: {
 *     dependentFilter: 'clientName',
 *     multiselect: true
 *   }
 * }
 */
export const EntityFilter = (
  filterParams: AgGridReact.CustomFilterProps & EntityFilterParams,
) => {
  const { dependentFilter, multiselect = false, onModelChange, getValue, api } = filterParams;

  // Always call hooks at the top level
  const [fetchDataVaultEntities, { data, loading }] = useGetDataVaultEntitiesLazyQuery();

  // Get client filter value for both modes
  const clientFilterValue = api.getColumnFilterModel(dependentFilter) as
    | string
    | string[]
    | undefined;

  // Handle both single and multiselect client filters
  const clientIds = Array.isArray(clientFilterValue)
    ? clientFilterValue
    : clientFilterValue
      ? [clientFilterValue]
      : [];

  // Extracting the entity list from the response (for non-multiselect mode)
  const entityLists: SearchOption[] = useMemo(() => {
    if (!data?.dataVaultEntities?.edges) return [];

    return data.dataVaultEntities.edges.map((edge: { node: { id: string; name: string } }) => ({
      id: edge.node.id,
      label: edge.node.name,
    }));
  }, [data]);

  // Fetching the entity info based on the dependent(arenaId) and the search term
  const fetchEntityInfo = useCallback(
    async (dependentId: string, searchTerm: string) => {
      await fetchDataVaultEntities({
        context: clientContext(),
        variables: {
          arenaIds: [dependentId], // Convert single ID to array
          searchTerm: searchTerm,
        },
        fetchPolicy: "no-cache",
      });
    },
    [fetchDataVaultEntities],
  );

  const handleSelect = useCallback(
    (entityId?: string) => {
      onModelChange(entityId || null);
    },
    [onModelChange],
  );

  const handleMultiSelect = useCallback(
    (entityIds: string[]) => {
      onModelChange(entityIds.length > 0 ? entityIds : null);
    },
    [onModelChange],
  );

  const doesFilterPass = useCallback(
    (params: AgGrid.IDoesFilterPassParams) => {
      const nodeValue = getValue(params.node) as string | undefined;
      const filterValue = filterParams.model as string | string[] | null;

      if (!filterValue) return true;

      // Get the client value for this row to check if it matches the selected clients
      const rowClientValue = (params.node.data as { clientId?: string })?.clientId;

      // Check if the row's client matches the selected clients
      const clientMatches = Array.isArray(clientFilterValue)
        ? clientFilterValue.length === 0 ||
          (rowClientValue && clientFilterValue.includes(rowClientValue))
        : !clientFilterValue || rowClientValue === clientFilterValue;

      // If client doesn't match, don't show this row
      if (!clientMatches) return false;

      // Handle multiselect mode (array of values)
      if (Array.isArray(filterValue)) {
        return nodeValue ? filterValue.includes(nodeValue) : false;
      }

      // Handle single select mode
      return nodeValue == filterValue;
    },
    [getValue, filterParams, clientFilterValue],
  );

  // Always register filter callbacks with the grid (for multiselect mode)
  AgGridReact.useGridFilter({ doesFilterPass });

  // If not multiselect, use the existing CustomFilter approach
  if (!multiselect) {
    return (
      <CustomFilter
        dependentFilter={dependentFilter}
        filterProps={filterParams}
        options={entityLists}
        isLoading={loading}
        infoText="Please select the client first"
        hintText="Search for an entity"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        optionsFetcher={fetchEntityInfo}
      />
    );
  }

  // Multiselect mode - use EntitySearch directly
  const currentValues = Array.isArray(filterParams.model) ? filterParams.model : [];

  return (
    <EntitySearch
      clientIds={clientIds}
      multiselect={multiselect}
      values={currentValues}
      onSelect={handleSelect}
      onMultiSelect={handleMultiSelect}
      placeholder="Search for entities"
    />
  );
};
